// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// THE SPREAD — what the survey average hides.
//
// Every other view on this site reports an average over the whole surveyed
// population. ABARES also publishes the same accounts split two ways: by farm
// size class, and by performance group. Splitting them shows that the site's
// headline is not a description of a typical farm at all.
//
// The finding this view exists to carry: the LAND adds a broadly similar
// number of percentage points to every group (2.6–3.8 a year on average). What
// separates the groups is almost entirely whether the FARMING earns anything.
// That is the same shape as the zone finding, at a completely different cut of
// the same data, which is why it is worth showing rather than asserting.
//
// ENCODING NOTE, and the reason this view does not use a ribbon between the two
// published rate series: the land's contribution goes NEGATIVE in six to nine
// of the covered years in every single group. A ribbon drawn between "rate
// excluding appreciation" and "rate including appreciation" would put ochre
// below the zero line, and the palette's colour-blind guarantee is that ochre
// is never below zero and crimson never above it — hue is redundant with
// position on every signed chart here. So the land's contribution is drawn as
// its own column FROM ZERO: ochre up, crimson down. Nothing is stacked.

import { loadCore, loadMeta, loadSpread } from '../data';
import type { BandYear, Spread } from '../types';
import { CRIMSON, FARMING, GRAPHITE, LAND, NEUTRAL, SCOPE_SHORT } from '../theme';
import { evidenceLines } from '../uncertainty';
import {
  axisBottom, axisLeft, brokenPath, chartSvg, el, extent, finite,
  hatchDefs, isolatedPoints, linearScale, markTip, niceTicks,
} from '../components/svg';
import { esc, fy, ha, money, num, pct, signedPct, tipAttr } from '../format';
import { closeButton, openOverlay } from '../overlay';
import { currentRoute, navigate, setParams } from '../router';
import { glossIcon, glossTerm } from '../components/glossary';

type CutId = 'size' | 'performance';

interface CutDef {
  id: CutId;
  /** ABARES' own band keys, ordered worst-to-best so the panels read upward. */
  keys: string[];
  labels: Record<string, string>;
  tab: string;
  noun: string;
  nounPlural: string;
  axisTitle: string;
}

const CUTS: Record<CutId, CutDef> = {
  size: {
    id: 'size',
    keys: ['Small', 'Medium', 'Large'],
    labels: { Small: 'Small farms', Medium: 'Medium farms', Large: 'Large farms' },
    tab: 'By farm size',
    noun: 'size class',
    nounPlural: 'size classes',
    axisTitle: '% of farm capital per year',
  },
  performance: {
    id: 'performance',
    keys: ['Low', 'Middle', 'High'],
    labels: { Low: 'Low performers', Middle: 'Middle performers', High: 'High performers' },
    tab: 'By performance',
    noun: 'performance group',
    nounPlural: 'performance groups',
    axisTitle: '% of farm capital per year',
  },
};

/** Unique ids per paint, so two hatch patterns in one view cannot collide. */
let patternSeq = 0;

/**
 * The size and performance files carry an RSE in the ABARES source, but the
 * data build does not currently carry it into spread.json — so unlike every
 * regional mark on this site, these marks cannot be tiered. That is stated on
 * every tooltip rather than papered over: an untiered mark drawn at full
 * strength alongside tiered ones would silently claim tier 1.
 */
const NO_TIER_LINE = 'Reliability: not carried for this breakdown.';
const bandEvidence = (): string[] => [...evidenceLines(null, null, null), NO_TIER_LINE];

interface BandStat {
  key: string;
  label: string;
  rows: BandYear[];
  meanEx: number | null;
  meanLand: number | null;
  meanInc: number | null;
  /** Years the mean is built from, and years dropped for any reason. */
  usedYears: number;
  droppedYears: number;
  negExYears: number;
  negLandYears: number;
  firstYear: number | null;
  lastYear: number | null;
}

export async function renderSpread(host: HTMLElement): Promise<void> {
  const [spread, core, meta] = await Promise.all([loadSpread(), loadCore(), loadMeta()]);

  // 2025 is provisional and is excluded from every mean, trend and ranking on
  // this site. These two breakdown files stop well short of it anyway (2023 and
  // 2022), but the guard is applied and its count printed regardless — a filter
  // that happens to drop nothing today is still the filter that must be there.
  const settled = new Set(core.settledYears);
  const nationalBy = new Map(core.national.map((n) => [n.year, n]));

  const route = currentRoute();
  let cut: CutId = route.params.get('cut') === 'performance' ? 'performance' : 'size';
  let focusYear: number | null = toYear(route.params.get('year'));
  let savedScroll = 0;

  host.innerHTML = `
    <div class="view-head">
      <h1>The Spread</h1>
      <p class="lede">Every headline on this site is an average across the whole survey. Split the
        surveyed ${glossTerm('broadacre', 'broadacre')} farms into groups and that average stops
        describing anybody: the land adds a broadly similar amount to every group, and what
        separates one group from another is almost entirely whether the farming earns anything.</p>
      <p>ABARES publishes the same farm accounts cut two ways — by farm size class, and by how the
        farms performed. Both cuts are shown here exactly as published; no rate of return is
        recalculated. ${glossIcon('rateOfReturn')}</p>
    </div>
  `;

  const controls = document.createElement('div');
  controls.className = 'controls';
  controls.innerHTML = `
    <div class="control">
      <span class="field-label" id="cut-label">Split the surveyed farms</span>
      <div class="seg" role="group" aria-labelledby="cut-label">
        <button type="button" data-cut="size">${esc(CUTS.size.tab)}</button>
        <button type="button" data-cut="performance">${esc(CUTS.performance.tab)}</button>
      </div>
    </div>
  `;
  controls.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest<HTMLButtonElement>('button[data-cut]');
    if (!btn) return;
    const next = btn.dataset.cut === 'performance' ? 'performance' : 'size';
    if (next === cut) return;
    cut = next;
    // A focused year from one cut may not exist in the other; drop it rather
    // than leave a spine hanging over a year that has no data.
    focusYear = null;
    savedScroll = 0;
    setParams({ cut, year: null });
    paint();
  });
  host.append(controls);

  const body = document.createElement('div');
  body.className = 'stack';
  host.append(body);

  const scope = document.createElement('p');
  scope.className = 'xsmall muted';
  scope.style.marginTop = 'var(--space-lg)';
  scope.textContent = SCOPE_SHORT;
  host.append(scope);

  paint();

  // ─────────────────────────────────────────────────────────────────────────

  function paint(): void {
    for (const b of controls.querySelectorAll<HTMLButtonElement>('button[data-cut]')) {
      b.setAttribute('aria-pressed', String(b.dataset.cut === cut));
    }

    const def = CUTS[cut];
    const stats = def.keys.map((k) => bandStat(spread, cut, k, def.labels[k], settled));
    const coveredYears = stats[0].rows.map((r) => r.year);
    const lastCovered = cut === 'size' ? spread.sizeLastYear : spread.performanceLastYear;
    const firstCovered = coveredYears[0] ?? core.years[0];

    // The national reference is averaged over EXACTLY the years this breakdown
    // covers, and only the settled ones. A 36-year national mean compared
    // against a 33-year band mean is not a comparison.
    const refYears = coveredYears.filter((y) => settled.has(y));
    const natVals = refYears
      .map((y) => nationalBy.get(y)?.rateEx)
      .filter((v): v is number => v != null && Number.isFinite(v));
    const natMeanEx = natVals.length ? natVals.reduce((a, b) => a + b, 0) / natVals.length : null;

    body.innerHTML = '';
    body.append(cutNote(def, stats, coveredYears));
    body.append(statRow(stats, natMeanEx, refYears.length));
    body.append(findingNote(stats));

    const panelCard = panelChart(def, stats, lastCovered, firstCovered);
    body.append(panelCard);

    body.append(engineChart(def, stats, natMeanEx, refYears.length));
    body.append(yearTable(def, stats, lastCovered));
    body.append(footerLinks());

    // Repainting for a year click would otherwise slam the wide chart back to
    // its left edge and lose the reader's place.
    const sc = panelCard.querySelector<HTMLElement>('.chart-scroll');
    if (sc && savedScroll) sc.scrollLeft = savedScroll;
    sc?.addEventListener('scroll', () => { savedScroll = sc.scrollLeft; });
  }

  // ── the explanatory note for the chosen cut ───────────────────────────────

  function cutNote(def: CutDef, stats: BandStat[], coveredYears: number[]): HTMLElement {
    const note = document.createElement('div');
    note.className = 'note';
    const first = coveredYears[0];
    const lastPop = lastYearWithPopulation(stats);
    const shareAt = (y: number | null, s: BandStat): string => {
      if (y == null) return '—';
      const total = stats.reduce((a, b) => a + (b.rows.find((r) => r.year === y)?.population ?? 0), 0);
      const v = s.rows.find((r) => r.year === y)?.population;
      if (!total || v == null) return '—';
      return pct((v / total) * 100, 0);
    };

    if (def.id === 'size') {
      note.innerHTML = `<strong>What the groups are.</strong> ABARES' own size classes for the
        surveyed farms. A farm can cross a class boundary without changing what it does, so a
        group's series is not a fixed set of farms: in ${esc(fy(first))} the Small class held
        ${esc(shareAt(first, stats[0]))} of the surveyed farms, and by ${esc(fy(lastPop))} it held
        ${esc(shareAt(lastPop, stats[0]))}, with the Large class going
        ${esc(shareAt(first, stats[2]))} → ${esc(shareAt(lastPop, stats[2]))}. Read the level of
        each group, not a group's own path through time.`;
    } else {
      note.innerHTML = `<strong>What the groups are.</strong> ABARES sorts the surveyed farms into
        three performance groups each year. By farm count these run about
        ${esc(shareAt(first, stats[0]))} / ${esc(shareAt(first, stats[1]))} /
        ${esc(shareAt(first, stats[2]))} and stay there
        (${esc(fy(lastPop))}: ${esc(shareAt(lastPop, stats[0]))} / ${esc(shareAt(lastPop, stats[1]))} /
        ${esc(shareAt(lastPop, stats[2]))}). Because the groups are <em>defined</em> by how the farms
        performed, a gap in return between them is partly definitional. What is not definitional is
        how big the gap is, and how much of each group's return comes from the land rather than
        from the farming.`;
    }
    return note;
  }

  // ── the three headline stats ──────────────────────────────────────────────

  function statRow(stats: BandStat[], natMeanEx: number | null, refCount: number): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'stat-row';
    for (const s of stats) {
      const card = document.createElement('div');
      card.className = 'stat';
      // Teal only above zero, crimson only below it, and neither on a missing
      // value — a coloured em dash would imply an estimate that does not exist.
      const cls = s.meanEx != null && s.meanEx > 0 ? 'farming' : '';
      const style = s.meanEx != null && s.meanEx < 0 ? `color:${CRIMSON}` : '';
      card.innerHTML = `
        <div class="stat-value ${cls}" style="${style}">${esc(signedPct(s.meanEx, 2))}</div>
        <div class="stat-label">${esc(s.label)} — mean return from farming</div>
        <div class="stat-note">Land added <span class="mono">${esc(signedPct(s.meanLand, 2))}</span> a year on top.
          ${esc(String(s.usedYears))} years averaged${s.droppedYears ? `, ${esc(String(s.droppedYears))} excluded` : ''}.</div>
      `;
      wrap.append(card);
    }
    const ref = document.createElement('div');
    ref.className = 'stat';
    ref.innerHTML = `
      <div class="stat-value">${esc(signedPct(natMeanEx, 2))}</div>
      <div class="stat-label">All surveyed farms, same years</div>
      <div class="stat-note">The site-wide average, over the same ${esc(String(refCount))} settled
        years — so it can be read against the three groups above.</div>
    `;
    wrap.append(ref);
    return wrap;
  }

  // ── the finding, computed rather than asserted ────────────────────────────

  function findingNote(stats: BandStat[]): HTMLElement {
    const lands = stats.map((s) => s.meanLand).filter((v): v is number => v != null);
    const exs = stats.map((s) => s.meanEx).filter((v): v is number => v != null);
    const note = document.createElement('div');
    note.className = 'note';
    if (!lands.length || !exs.length) { note.textContent = 'No group carries a published mean.'; return note; }
    const landLo = Math.min(...lands); const landHi = Math.max(...lands);
    const exLo = Math.min(...exs); const exHi = Math.max(...exs);
    const negs = stats.map((s) => s.negLandYears);
    const negLo = Math.min(...negs); const negHi = Math.max(...negs);
    const covered = Math.max(...stats.map((s) => s.usedYears));
    const negLand = negLo === negHi ? `${negLo}` : `${negLo} to ${negHi}`;
    note.innerHTML = `<strong>The land pays about the same for everybody.</strong> Averaged over the
      covered years, ${glossTerm('capitalAppreciation', 'capital appreciation')} added between
      <span class="mono">${esc(signedPct(landLo, 2))}</span> and
      <span class="mono">${esc(signedPct(landHi, 2))}</span> a year to <em>every</em> group — a spread of
      ${esc((landHi - landLo).toFixed(2))} percentage points. The farming return ranged from
      <span class="mono">${esc(signedPct(exLo, 2))}</span> to
      <span class="mono">${esc(signedPct(exHi, 2))}</span> — a spread of
      ${esc((exHi - exLo).toFixed(2))} points, several times wider. The groups differ in what the
      farming earns, not in what the land does. And the land is not a one-way bet: in every group it
      <em>lost</em> value in ${esc(negLand)} of the ${esc(String(covered))} covered years.`;
    return note;
  }

  // ── chart 1: three small-multiple panels ──────────────────────────────────

  function panelChart(def: CutDef, stats: BandStat[], lastCovered: number, firstCovered: number): HTMLElement {
    const card = document.createElement('div');
    card.className = 'card';

    const head = document.createElement('div');
    head.innerHTML = `
      <h2 class="card-title">Two engines, one group at a time</h2>
      <p class="card-sub">Three panels, one per ${esc(def.noun)}, on a shared scale so they can be
        compared by eye. In each panel the teal line is the ${glossTerm('rateOfReturn', 'rate of return')}
        the farming earned by itself, year by year. The columns underneath are what the land added
        that year, drawn from zero: ochre when the land gained value, crimson when it lost value.
        The two are separate published series and are never stacked. Hover or select a year to read
        all three groups at once.</p>
    `;
    card.append(head);

    const W = 880; const ML = 54; const MR = 16;
    const PH = 148; const GAP = 32; const TOP = 16;
    const AXIS = 40;
    const H = TOP + stats.length * PH + (stats.length - 1) * GAP + AXIS;

    const years = core.years;
    const xs = linearScale([years[0] - 0.5, years[years.length - 1] + 0.5], [ML, W - MR]);
    const step = (W - MR - ML) / years.length;
    const bw = Math.max(4, Math.min(13, step * 0.6));

    // The shared y-domain must hold every mark that will be drawn: both series
    // for every band, plus the national reference line.
    const all: (number | null)[] = [];
    for (const s of stats) {
      for (const r of s.rows) {
        all.push(r.rateEx);
        all.push(landPoints(r));
      }
    }
    for (const y of years) if (settled.has(y)) all.push(nationalBy.get(y)?.rateEx ?? null);
    const [lo, hi] = extent(all);
    const pad = Math.max(1, (hi - lo) * 0.08);
    // 7 rather than 5: the domain is set by the widest land columns (roughly
    // ±17 points), and at 5 the nicing lands on a step of 10, which leaves the
    // teal line floating between two gridlines with nothing to read it against.
    const yTicks = niceTicks(lo - pad, hi + pad, 7);
    const dom: [number, number] = [Math.min(lo - pad, ...yTicks), Math.max(hi + pad, ...yTicks)];

    const svg = chartSvg(W, H, `Rate of return from farming, and the land's contribution, for each ${def.noun}, ${fy(firstCovered)} to ${fy(lastCovered)}`);
    svg.style.minWidth = `${W}px`;

    const hatchId = `spread-gap-${++patternSeq}`;
    svg.append(hatchDefs(hatchId, NEUTRAL, 0.55));

    const panelTop = (i: number) => TOP + i * (PH + GAP);

    stats.forEach((s, i) => {
      const top = panelTop(i);
      const bottom = top + PH;
      const ysc = linearScale(dom, [bottom, top]);
      const g = el('g');

      // The unit label goes on the middle panel only: three rotated copies of
      // the same sentence is noise, and the scale is shared anyway.
      axisLeft(g, ysc, ML, {
        ticks: yTicks, format: fmtPts, gridTo: W - MR,
        title: i === 1 ? def.axisTitle : undefined,
      });

      // The strip ABARES has not published this breakdown for. Hatched and
      // labelled, never bridged and never zero-filled.
      const gapX = finite(xs(lastCovered + 0.5), W - MR);
      if (gapX < W - MR - 1) {
        g.append(el('rect', {
          x: gapX, y: top, width: Math.max(0, W - MR - gapX), height: PH,
          fill: `url(#${hatchId})`, 'aria-hidden': 'true',
        }));
        g.append(el('line', { x1: gapX, y1: top, x2: gapX, y2: bottom, stroke: NEUTRAL, 'stroke-width': 1, 'stroke-dasharray': '3 3', 'aria-hidden': 'true' }));
      }

      // 1. the land's contribution, as columns from zero.
      const zeroY = finite(ysc(0));
      for (const r of s.rows) {
        const v = landPoints(r);
        if (v == null) continue;
        const yv = finite(ysc(v));
        const x = finite(xs(r.year)) - bw / 2;
        const yTop = Math.min(yv, zeroY);
        const hgt = Math.abs(yv - zeroY);
        if (!Number.isFinite(x) || !Number.isFinite(hgt)) continue;
        const rect = el('rect', {
          class: 'mark', x, y: yTop, width: bw, height: Math.max(0.8, hgt),
          fill: v >= 0 ? LAND : CRIMSON, 'pointer-events': 'none',
        });
        markTip(rect, [
          `${s.label}, ${fy(r.year)}`,
          `Land ${v >= 0 ? 'added' : 'took away'} ${signedPct(v, 1)} of farm capital`,
          ...bandEvidence(),
        ]);
        g.append(rect);
      }

      // 2. the farming's own rate of return, as a line that BREAKS at any gap.
      const pts = years.map((y) => {
        const row = s.rows.find((r) => r.year === y);
        const v = row?.rateEx;
        return { x: finite(xs(y)), y: v == null || !Number.isFinite(v) ? null : finite(ysc(v)) };
      });
      for (const d of brokenPath(pts)) {
        g.append(el('path', { d, fill: 'none', stroke: FARMING, 'stroke-width': 2.4, 'stroke-linejoin': 'round', 'pointer-events': 'none' }));
      }
      // A year that survives alone between two gaps would otherwise vanish.
      for (const p of isolatedPoints(pts)) {
        g.append(el('circle', { cx: p.x, cy: p.y, r: 2.6, fill: FARMING, 'pointer-events': 'none' }));
      }

      // 3. the national reference: dashed graphite, settled years only.
      const natPts = years.map((y) => {
        const v = settled.has(y) ? nationalBy.get(y)?.rateEx : null;
        return { x: finite(xs(y)), y: v == null || !Number.isFinite(v) ? null : finite(ysc(v)) };
      });
      for (const d of brokenPath(natPts)) {
        g.append(el('path', { d, fill: 'none', stroke: GRAPHITE, 'stroke-width': 1.2, 'stroke-dasharray': '5 4', opacity: 0.75, 'pointer-events': 'none' }));
      }
      // The provisional year is drawn, but only as a hollow dashed token, and it
      // is in no mean, trend or fitted line anywhere in this view.
      const prov = core.provisionalYear;
      const provV = prov == null ? null : nationalBy.get(prov)?.rateEx;
      if (prov != null && provV != null && Number.isFinite(provV)) {
        const c = el('circle', {
          cx: finite(xs(prov)), cy: finite(ysc(provV)), r: 3.2,
          fill: 'none', stroke: GRAPHITE, 'stroke-width': 1.2, 'stroke-dasharray': '2 2', 'pointer-events': 'none',
        });
        markTip(c, [`All surveyed farms, ${fy(prov)}: ${signedPct(provV, 1)} from farming`, 'Provisional — excluded from every mean and trend on this site.']);
        g.append(c);
      }

      // panel title + its mean, inside the plot so it travels with the panel
      const t = el('text', { x: ML + 6, y: top + 14, class: 'axis-title' });
      t.textContent = s.label;
      g.append(t);
      const sub = el('text', { x: ML + 6, y: top + 28, class: 'axis-text' });
      sub.textContent = `farming ${signedPct(s.meanEx, 2)} · land ${signedPct(s.meanLand, 2)} · mean of ${s.usedYears} years`;
      g.append(sub);

      svg.append(g);
    });

    axisBottom(svg, xs, panelTop(stats.length - 1) + PH, {
      ticks: years.filter((y) => y % 5 === 0 || y === years[0]),
      format: (v) => fy(v),
      title: 'Financial year (the year each financial year ends)',
    });

    // 4. the year spine: one focusable hit surface per covered year, spanning
    //    all three panels, because comparing the groups within a year is the
    //    whole reason this view exists.
    const spineTop = panelTop(0);
    const spineBottom = panelTop(stats.length - 1) + PH;
    const spines = el('g');
    for (const y of stats[0].rows.map((r) => r.year)) {
      const cx = finite(xs(y));
      const rect = el('rect', {
        x: cx - step / 2, y: spineTop, width: step, height: spineBottom - spineTop,
        fill: 'transparent', 'pointer-events': 'all', tabindex: 0, role: 'button',
        class: focusYear === y ? 'mark sel' : 'mark',
      });
      const lines: string[] = [fy(y)];
      for (const s of stats) {
        const r = s.rows.find((q) => q.year === y);
        const lp = r ? landPoints(r) : null;
        lines.push(`${s.label}: farming ${signedPct(r?.rateEx ?? null, 1)}, land ${signedPct(lp, 1)}, together ${signedPct(r?.rateInc ?? null, 1)}`);
      }
      const nat = nationalBy.get(y)?.rateEx;
      if (nat != null) lines.push(`All surveyed farms: ${signedPct(nat, 1)} from farming`);
      lines.push(...bandEvidence());
      lines.push('Select to pin this year.');
      markTip(rect, lines);
      rect.addEventListener('click', () => setFocus(y));
      rect.addEventListener('keydown', (e) => {
        const k = (e as KeyboardEvent).key;
        if (k !== 'Enter' && k !== ' ') return;
        e.preventDefault();
        setFocus(y);
      });
      spines.append(rect);

      if (focusYear === y) {
        spines.insertBefore(el('line', {
          x1: cx, y1: spineTop, x2: cx, y2: spineBottom,
          stroke: GRAPHITE, 'stroke-width': 1.6, 'pointer-events': 'none', 'aria-hidden': 'true',
        }), rect);
      }
    }
    svg.append(spines);

    const frame = document.createElement('div');
    frame.className = 'chart-frame';
    const scroll = document.createElement('div');
    scroll.className = 'chart-scroll';
    scroll.append(svg);
    frame.append(scroll);
    card.append(frame);

    const legend = document.createElement('div');
    legend.className = 'legend';
    legend.innerHTML = `
      <span class="legend-item"><span class="legend-swatch" style="background:${FARMING};height:3px;border-radius:0"></span> what the farming earned</span>
      <span class="legend-item"><span class="legend-swatch" style="background:${LAND}"></span> what the land added that year</span>
      <span class="legend-item"><span class="legend-swatch" style="background:${CRIMSON}"></span> the land lost value that year</span>
      <span class="legend-item"><span class="legend-swatch" style="background:${GRAPHITE};height:3px;border-radius:0"></span> all surveyed farms (dashed)</span>
      <span class="legend-item"><span class="legend-swatch" style="background:${NEUTRAL};opacity:.45"></span> not published in this breakdown</span>
    `;
    card.append(legend);

    const readout = document.createElement('div');
    readout.className = 'note caveat';
    readout.setAttribute('aria-live', 'polite');
    card.append(readout);
    fillReadout(readout, stats, def);

    const cov = document.createElement('p');
    cov.className = 'xsmall muted caveat';
    cov.textContent = `This breakdown is published only to ${fy(lastCovered)} — the size split ends `
      + `${fy(meta.spreadCoverage.sizeLastYear)} and the performance split ends `
      + `${fy(meta.spreadCoverage.performanceLastYear)}, while the all-farm series runs to `
      + `${fy(meta.lastYear)}. The hatched strip is those unpublished years; nothing is interpolated `
      + `across them. ${fy(core.provisionalYear ?? meta.lastYear)} is provisional and is in no mean or trend here.`;
    card.append(cov);

    return card;
  }

  function fillReadout(node: HTMLElement, stats: BandStat[], def: CutDef): void {
    if (focusYear == null) {
      node.innerHTML = `<strong>Pin a year.</strong> Select any year in the chart above — by click or
        by keyboard — to read all three ${esc(def.nounPlural)} side by side for that year.`;
      return;
    }
    const y = focusYear;
    const parts = stats.map((s) => {
      const r = s.rows.find((q) => q.year === y);
      const lp = r ? landPoints(r) : null;
      return `<li><strong>${esc(s.label)}:</strong> farming
        <span class="mono">${esc(signedPct(r?.rateEx ?? null, 1))}</span>, land
        <span class="mono">${esc(signedPct(lp, 1))}</span>, together
        <span class="mono">${esc(signedPct(r?.rateInc ?? null, 1))}</span></li>`;
    }).join('');
    const nat = nationalBy.get(y)?.rateEx ?? null;
    node.innerHTML = `<strong>${esc(fy(y))}</strong>
      <ul style="margin:.25rem 0 .25rem 1.1rem">${parts}</ul>
      All surveyed farms that year: <span class="mono">${esc(signedPct(nat, 1))}</span> from farming.
      <button type="button" class="btn btn-sm" data-clear-year="1" style="margin-left:.5rem">Clear pinned year</button>`;
    node.querySelector<HTMLButtonElement>('[data-clear-year]')?.addEventListener('click', () => setFocus(y));
  }

  function setFocus(y: number): void {
    focusYear = focusYear === y ? null : y;
    setParams({ year: focusYear });
    paint();
  }

  // ── chart 2: the whole period as six bars ─────────────────────────────────

  function engineChart(def: CutDef, stats: BandStat[], natMeanEx: number | null, refCount: number): HTMLElement {
    const card = document.createElement('div');
    card.className = 'card';
    card.innerHTML = `
      <h2 class="card-title">The whole period, as six bars</h2>
      <p class="card-sub">Each ${esc(def.noun)} gets two bars, both measured from zero and both in
        percentage points of farm capital a year: the top bar is the mean return the farming earned,
        the bottom bar is the mean the land added. They are two separate published averages sitting
        side by side, not a stack. The bar to watch is how far the top bars spread apart while the
        bottom bars stay level.</p>
    `;

    // ML leaves room for the group name; MR leaves room for the value label
    // that sits outside the end of each bar (on the left, for negative bars).
    const W = 790; const ML = 172; const MR = 80; const ROW = 66; const TOPP = 10;
    const H = TOPP + stats.length * ROW + 42;
    const vals: (number | null)[] = [0, natMeanEx];
    for (const s of stats) { vals.push(s.meanEx); vals.push(s.meanLand); }
    const [lo, hi] = extent(vals);
    const ticks = niceTicks(Math.min(lo, 0) * 1.12, Math.max(hi, 0) * 1.12, 5);
    const xs = linearScale(
      [Math.min(...ticks, lo, 0), Math.max(...ticks, hi, 0)],
      [ML, W - MR],
    );
    const zero = finite(xs(0));

    const svg = chartSvg(W, H, `Mean return from farming and mean contribution from land, for each ${def.noun}`);
    svg.style.minWidth = `${W}px`;

    for (const t of ticks) {
      const x = finite(xs(t));
      svg.append(el('line', { class: t === 0 ? 'zero-line' : 'grid-line', x1: x, y1: TOPP, x2: x, y2: TOPP + stats.length * ROW }));
    }

    stats.forEach((s, i) => {
      const top = TOPP + i * ROW;
      const name = el('text', { x: 0, y: top + 24, class: 'axis-title' });
      name.textContent = s.label;
      svg.append(name);
      const sub = el('text', { x: 0, y: top + 40, class: 'axis-text' });
      sub.textContent = `${s.usedYears} years averaged`;
      svg.append(sub);

      const bars: Array<{ v: number | null; label: string; colour: string; neg: string; y: number }> = [
        { v: s.meanEx, label: 'what the farming earned', colour: FARMING, neg: CRIMSON, y: top + 10 },
        { v: s.meanLand, label: 'what the land added', colour: LAND, neg: CRIMSON, y: top + 34 },
      ];
      for (const b of bars) {
        if (b.v == null || !Number.isFinite(b.v)) continue;
        const x1 = finite(xs(b.v));
        const bx = Math.min(zero, x1);
        const bwid = Math.max(1, Math.abs(x1 - zero));
        // Ochre only ever appears above zero, crimson only below — so hue stays
        // redundant with which side of the zero rule the bar sits on.
        const rect = el('rect', {
          class: 'mark', x: bx, y: b.y, width: bwid, height: 18,
          fill: b.v >= 0 ? b.colour : b.neg, tabindex: 0, role: 'button',
        });
        markTip(rect, [
          `${s.label} — ${b.label}: ${signedPct(b.v, 2)} a year`,
          `Mean of ${s.usedYears} settled years, ${fy(s.firstYear ?? 0)} to ${fy(s.lastYear ?? 0)}${s.droppedYears ? `; ${s.droppedYears} excluded` : ''}`,
          `Together: ${signedPct(s.meanInc, 2)} a year`,
          ...bandEvidence(),
          'Select to open the full year-by-year table.',
        ]);
        rect.addEventListener('click', () => openBand(def, s));
        rect.addEventListener('keydown', (e) => {
          const k = (e as KeyboardEvent).key;
          if (k !== 'Enter' && k !== ' ') return;
          e.preventDefault();
          openBand(def, s);
        });
        svg.append(rect);

        const lab = el('text', {
          x: b.v >= 0 ? x1 + 6 : x1 - 6, y: b.y + 13,
          class: 'axis-text', 'text-anchor': b.v >= 0 ? 'start' : 'end', 'pointer-events': 'none',
        });
        lab.textContent = signedPct(b.v, 2);
        svg.append(lab);
      }
    });

    if (natMeanEx != null && Number.isFinite(natMeanEx)) {
      const x = finite(xs(natMeanEx));
      const line = el('line', {
        x1: x, y1: TOPP - 4, x2: x, y2: TOPP + stats.length * ROW,
        stroke: GRAPHITE, 'stroke-width': 1.6, 'stroke-dasharray': '5 4',
      });
      markTip(line, [
        `All surveyed farms: ${signedPct(natMeanEx, 2)} a year from farming`,
        `Mean of the same ${refCount} settled years this breakdown covers`,
      ]);
      svg.append(line);
    }

    axisBottom(svg, xs, TOPP + stats.length * ROW, { ticks, format: fmtPts, title: def.axisTitle });

    const frame = document.createElement('div');
    frame.className = 'chart-frame';
    const scroll = document.createElement('div');
    scroll.className = 'chart-scroll';
    scroll.append(svg);
    frame.append(scroll);
    card.append(frame);

    const legend = document.createElement('div');
    legend.className = 'legend';
    legend.innerHTML = `
      <span class="legend-item"><span class="legend-swatch" style="background:${FARMING}"></span> mean return from farming</span>
      <span class="legend-item"><span class="legend-swatch" style="background:${LAND}"></span> mean added by the land</span>
      <span class="legend-item"><span class="legend-swatch" style="background:${CRIMSON}"></span> below zero</span>
      <span class="legend-item"><span class="legend-swatch" style="background:${GRAPHITE};height:3px;border-radius:0"></span> all surveyed farms, same years (dashed)</span>
    `;
    card.append(legend);

    const nb = document.createElement('p');
    nb.className = 'xsmall muted caveat';
    nb.textContent = 'Select any bar for that group\'s full year-by-year table. Both averages are '
      + 'means of the annual rates ABARES publishes; no rate is recomputed from profit and capital. '
      + 'The land\'s contribution is the published rate including capital appreciation minus the '
      + 'published rate excluding it — a difference between two series, never a share, because a '
      + 'share of a negative return is meaningless and several of these groups have one.';
    card.append(nb);

    return card;
  }

  // ── the latest published year, group by group ─────────────────────────────

  function yearTable(def: CutDef, stats: BandStat[], lastCovered: number): HTMLElement {
    const card = document.createElement('div');
    card.className = 'card';
    card.innerHTML = `
      <h2 class="card-title">Each group in ${esc(fy(lastCovered))}</h2>
      <p class="card-sub">The last year this breakdown is published for, ${glossTerm('perFarm', 'per surveyed farm')}
        and in ${glossTerm('nominal', 'nominal dollars')}. Empty cells are values ABARES does not publish
        for this split — they are not zeroes. Select a row for the full history.</p>
    `;

    const rows = stats.map((s) => {
      const r = s.rows.find((q) => q.year === lastCovered);
      const lp = r ? landPoints(r) : null;
      return `<tr class="clickable" tabindex="0" data-band="${esc(s.key)}"
          data-tip="${tipAttr(`${s.label}, ${fy(lastCovered)}`, 'Select for every published year for this group.', ...bandEvidence())}">
        <th scope="row">${esc(s.label)}</th>
        ${cell(r?.population, (v) => num(v))}
        ${cellSigned(r?.rateEx)}
        ${cellSigned(lp)}
        ${cellSigned(r?.rateInc)}
        ${cell(r?.receipts, (v) => money(v))}
        ${cell(r?.costs, (v) => money(v))}
        ${cell(r?.cashIncome, (v) => money(v))}
        ${cell(r?.businessProfit, (v) => money(v))}
        ${cell(r?.landValue, (v) => money(v))}
        ${cell(r?.debt, (v) => money(v))}
        ${cell(r?.equityRatio, (v) => pct(v, 0))}
        ${cell(r?.area, (v) => ha(v))}
      </tr>`;
    }).join('');

    const scroll = document.createElement('div');
    scroll.className = 'table-scroll';
    scroll.innerHTML = `
      <table>
        <caption class="visually-hidden">Each of the three ${esc(def.nounPlural)} in ${esc(fy(lastCovered))}</caption>
        <thead><tr>
          <th scope="col">Group</th>
          <th scope="col" class="num">Farms surveyed</th>
          <th scope="col" class="num">Farming</th>
          <th scope="col" class="num">Land</th>
          <th scope="col" class="num">Together</th>
          <th scope="col" class="num">Cash receipts</th>
          <th scope="col" class="num">Cash costs</th>
          <th scope="col" class="num">Cash income</th>
          <th scope="col" class="num">Business profit</th>
          <th scope="col" class="num">Land value</th>
          <th scope="col" class="num">Debt</th>
          <th scope="col" class="num">Equity</th>
          <th scope="col" class="num">Area</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
    `;
    const openFromRow = (target: Element | null) => {
      const tr = target?.closest<HTMLElement>('tr[data-band]');
      if (!tr) return;
      const s = stats.find((q) => q.key === tr.dataset.band);
      if (s) openBand(def, s);
    };
    scroll.addEventListener('click', (e) => openFromRow(e.target as Element));
    scroll.addEventListener('keydown', (e) => {
      const k = (e as KeyboardEvent).key;
      if (k !== 'Enter' && k !== ' ') return;
      e.preventDefault();
      openFromRow(e.target as Element);
    });
    card.append(scroll);

    const nb = document.createElement('p');
    nb.className = 'xsmall muted caveat';
    nb.innerHTML = `"Farming" is the ${glossTerm('rateOfReturn', 'rate of return')} excluding
      ${glossTerm('capitalAppreciation', 'capital appreciation')}; "Land" is what appreciation added,
      in percentage points; "Together" is the published rate including it.
      ${glossTerm('farmCashIncome', 'Farm cash income')} and
      ${glossTerm('farmBusinessProfit', 'farm business profit')} are dollars per surveyed farm, and
      ${glossTerm('equityRatio', 'equity')} is the share of the business the owners hold outright.`;
    card.append(nb);

    return card;
  }

  // ── the per-group drawer ──────────────────────────────────────────────────

  function openBand(def: CutDef, s: BandStat): void {
    openOverlay({
      panelClass: 'drawer',
      label: `${s.label}: every published year`,
      lockScroll: true,
      build(panel, close) {
        const head = document.createElement('div');
        head.className = 'overlay-head';
        const h = document.createElement('h2');
        h.textContent = `${s.label} — ${def.tab.toLowerCase()}`;
        head.append(h, closeButton(close, `Close the ${s.label} detail`));
        panel.append(head);

        const intro = document.createElement('p');
        intro.className = 'small muted';
        intro.textContent = `Every year ABARES publishes this ${def.noun} for: ${fy(s.firstYear ?? 0)} to `
          + `${fy(s.lastYear ?? 0)}. Mean return from farming ${signedPct(s.meanEx, 2)} a year; mean added by `
          + `the land ${signedPct(s.meanLand, 2)}. Farming was below zero in ${s.negExYears} of `
          + `${s.usedYears} years, and the land lost value in ${s.negLandYears}. `
          + `${s.droppedYears} years were excluded from those means (provisional or unpublished). `
          + `${NO_TIER_LINE}`;
        panel.append(intro);

        const body2 = document.createElement('div');
        body2.className = 'table-scroll';
        body2.innerHTML = `
          <table>
            <thead><tr>
              <th scope="col">Year</th>
              <th scope="col" class="num">Farming</th>
              <th scope="col" class="num">Land</th>
              <th scope="col" class="num">Together</th>
              <th scope="col" class="num">Cash income</th>
              <th scope="col" class="num">Business profit</th>
              <th scope="col" class="num">Land value</th>
              <th scope="col" class="num">Debt</th>
              <th scope="col" class="num">Farms</th>
            </tr></thead>
            <tbody>${s.rows.map((r) => `<tr>
              <th scope="row" class="mono">${esc(fy(r.year))}</th>
              ${cellSigned(r.rateEx)}
              ${cellSigned(landPoints(r))}
              ${cellSigned(r.rateInc)}
              ${cell(r.cashIncome, (v) => money(v, { long: true }))}
              ${cell(r.businessProfit, (v) => money(v, { long: true }))}
              ${cell(r.landValue, (v) => money(v))}
              ${cell(r.debt, (v) => money(v))}
              ${cell(r.population, (v) => num(v))}
            </tr>`).join('')}</tbody>
          </table>`;
        panel.append(body2);
      },
    });
  }

  function footerLinks(): HTMLElement {
    const row = document.createElement('div');
    row.className = 'row';
    row.style.flexWrap = 'wrap';
    const a = document.createElement('button');
    a.type = 'button';
    a.className = 'btn btn-sm';
    a.textContent = 'See the national ledger';
    a.addEventListener('click', () => navigate('ledger'));
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'btn btn-sm';
    b.textContent = 'See the two engines nationally';
    b.addEventListener('click', () => navigate('engines'));
    row.append(a, b);
    return row;
  }
}

// ─────────────────────────── pure helpers ───────────────────────────────────

/**
 * The land's contribution, in percentage points.
 *
 * This is the difference between two series ABARES publishes side by side —
 * exactly how the zone summaries derive `appreciationPoints`. It is deliberately
 * NOT expressed as a share of the total: several of these groups have a negative
 * return from farming, and a "share" with a negative numerator or a negative
 * denominator produces numbers like 138% and −124%, which are arithmetic noise
 * rather than findings.
 */
function landPoints(r: BandYear): number | null {
  if (r.rateEx == null || r.rateInc == null) return null;
  if (!Number.isFinite(r.rateEx) || !Number.isFinite(r.rateInc)) return null;
  return r.rateInc - r.rateEx;
}

function bandStat(sp: Spread, cut: CutId, key: string, label: string, settled: Set<number>): BandStat {
  const rows = (cut === 'size' ? sp.size : sp.performance)[key] ?? [];
  const usable = rows.filter((r) => settled.has(r.year) && r.rateEx != null && r.rateInc != null);
  const dropped = rows.length - usable.length;
  const mean = (pick: (r: BandYear) => number | null): number | null => {
    const vs = usable.map(pick).filter((v): v is number => v != null && Number.isFinite(v));
    return vs.length ? vs.reduce((a, b) => a + b, 0) / vs.length : null;
  };
  return {
    key,
    label,
    rows,
    meanEx: mean((r) => r.rateEx),
    meanInc: mean((r) => r.rateInc),
    meanLand: mean(landPoints),
    usedYears: usable.length,
    droppedYears: dropped,
    negExYears: usable.filter((r) => (r.rateEx ?? 0) < 0).length,
    negLandYears: usable.filter((r) => (landPoints(r) ?? 0) < 0).length,
    firstYear: rows.length ? rows[0].year : null,
    lastYear: rows.length ? rows[rows.length - 1].year : null,
  };
}

function lastYearWithPopulation(stats: BandStat[]): number | null {
  const base = stats[0]?.rows ?? [];
  for (let i = base.length - 1; i >= 0; i--) {
    const y = base[i].year;
    if (stats.every((s) => s.rows.find((r) => r.year === y)?.population != null)) return y;
  }
  return null;
}

function toYear(v: string | null): number | null {
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const fmtPts = (v: number): string => `${v > 0 ? '+' : ''}${v.toFixed(0)}%`;

/** A missing survey value renders as an em dash. It is never a zero. */
function cell(v: number | null | undefined, f: (x: number) => string): string {
  if (v == null || !Number.isFinite(v)) return '<td class="num muted">—</td>';
  return `<td class="num">${esc(f(v))}</td>`;
}

function cellSigned(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '<td class="num muted">—</td>';
  return `<td class="num ${v < 0 ? 'neg' : 'pos'}">${esc(signedPct(v, 1))}</td>`;
}
